import { DryingProjectOverview } from "@/data/models/DryingProjectOverview";

/**
 * How much of the archive has arrived, reported once per slice by
 * `observeArchivedProjects()`.
 */
export interface ArchiveLoadProgress {
  /** Distinct overview page documents that have arrived. */
  loadedPages: number;
  /**
   * The projects merged out of them so far.
   *
   * Handed over rather than counted here so the caller can count them through its **own** filters.
   * The raw number is not what the list will show: the archive collection also holds the deleted
   * projects, which the default view excludes, so a raw count climbs past the final total and then
   * drops back to it.
   */
  projects: DryingProjectOverview[];
  /** True once the last slice has reported, so nothing further is on its way. */
  isComplete: boolean;
}

/**
 * The percentage to draw, or `null` when there is nothing to measure against and the bar has to
 * run indeterminate.
 *
 * Capped below 100 until the load actually reports complete, so a bar that has run out of
 * denominator never claims to be finished: an archive that grew since the count was taken simply
 * holds at the cap for its last slices.
 *
 * **The denominator is advisory and may be wrong in either direction**, which is why this caps and
 * holds rather than trusting it. It is counted server-side per load
 * (`fetchArchivedProjectOverviewPageCount`), so it is normally exact, but it is taken before the
 * load finishes and the archive can grow underneath it.
 */
export function calculateArchiveLoadPercentage(
  progress: ArchiveLoadProgress,
  expectedPages: number | null
): number | null {
  if (progress.isComplete) {
    return 100;
  }

  if (expectedPages === null || expectedPages <= 0) {
    return null;
  }

  return Math.min(99, Math.round((progress.loadedPages / expectedPages) * 100));
}
